import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useQuoteExpiry } from "@/shared/hooks/useQuoteExpiry";
import { useTwoFactorAction } from "@/shared/hooks/useTwoFactorAction";
import useTranslations from "@/shared/lang/useTranslations";

import {
  EMPTY_DESTINATION,
  sameDestination,
  type DestinationValues,
} from "../components/swap/destination.schema";
import {
  MAX_AUTO_REFRESHES,
  QUOTE_SUBMIT_GUARD_MS,
  SAVED_WALLET_ALIAS_MAX_LENGTH,
} from "../constants/cryptoSwaps.constants";
import { CryptoSellService } from "../services/cryptoSell.service";
import { CryptoSwapsService } from "../services/cryptoSwaps.service";
import type { OtcSellPair } from "../types/cryptoSell.types";
import type { OtcAmountAnchor, OtcSavedWallet } from "../types/cryptoSwaps.types";
import { isSellQuoteSpent, normalizeSellCreateError } from "../utils/cryptoSellErrors";
import { sellConverterQuote } from "../utils/cryptoSellStatus";
import {
  checkCryptoSwapAddress,
  defaultNetwork,
  isSupportedNetwork,
  defaultWalletAlias,
} from "../utils/cryptoSwapAddress";
import {
  clientAmountIssue,
  type AmountIssue,
  type AmountIssueContext,
} from "../utils/cryptoSwapAmountIssues";
import { canonicalAmount, type AmountBounds } from "../utils/cryptoSwapAmounts";
import { converterField } from "../utils/cryptoSwapConverter";
import {
  isRangeRejection,
  QUOTE_LAPSED_ERROR,
  type NormalizedCryptoSwapError,
} from "../utils/cryptoSwapErrors";
import { amountGate, gatedQuoteAmount, receiveBoundsOf, sendBoundsOf } from "../utils/cryptoSwapLimits";
import { cryptoSellOrderPath } from "../utils/cryptoSwapLinks";
import { isNewQuoteRequest, type RequestedQuote } from "../utils/cryptoSwapQuoteScheduler";
import {
  quoteDeadlineMs,
  resolveCryptoSwapBlock,
  resolveSubmitBlock,
  type CryptoSwapBlock,
} from "../utils/cryptoSwapStatus";
import type { CryptoSwapSubmitPhase, DestinationIssue } from "./useCryptoSwapForm";
import { useOtcQuote } from "./useOtcQuote";

export interface UseCryptoSellFormOptions {
  pairs: OtcSellPair[];
  canWrite: boolean;
  /** INTERNAL staff act on behalf of the organization they pick; members are pinned server-side. */
  isStaff: boolean;
  organizationId: string | null;
  /** A wallet saved on the way to the sale, so the list shows it next time. */
  onWalletSaved?: (wallet: OtcSavedWallet) => void;
}

/**
 * The sale form: how much, on which network, from which wallet. Pricing, expiry and the review gate behave
 * exactly like the purchase's; the network is the customer's choice and part of the price.
 */
export function useCryptoSellForm({
  pairs,
  canWrite,
  isStaff,
  organizationId,
  onWalletSaved,
}: UseCryptoSellFormOptions) {
  const { t } = useTranslations("cryptoSwaps");
  const navigate = useNavigate();
  const saveWalletTwoFactor = useTwoFactorAction<OtcSavedWallet>();
  const { start: startSaveWallet } = saveWalletTwoFactor;

  const [chosenNetwork, setChosenNetwork] = useState("");
  const [anchor, setAnchor] = useState<OtcAmountAnchor>("send");
  const [amount, setAmount] = useState("");
  const [refund, setRefundState] = useState<DestinationValues>(EMPTY_DESTINATION);
  const [refundValid, setRefundValid] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  /** The quote id the rules were acknowledged for: a new price must be read and acknowledged again. */
  const [acknowledgedFor, setAcknowledgedFor] = useState<string | null>(null);
  const [submitPhase, setSubmitPhase] = useState<CryptoSwapSubmitPhase>("idle");
  const [submitError, setSubmitError] = useState<NormalizedCryptoSwapError | null>(null);
  const [expiredQuote, setExpiredQuote] = useState<{ id: string; atMs: number } | null>(null);
  const [manualTick, requestManualQuote] = useReducer((tick: number) => tick + 1, 0);

  const quote = useOtcQuote(CryptoSellService.createQuote);

  const pair = pairs[0] ?? null;
  const networks = useMemo(() => (pair?.networks ?? []).filter(isSupportedNetwork), [pair]);
  const network = networks.includes(chosenNetwork) ? chosenNetwork : defaultNetwork(networks);
  const organizationReady = !isStaff || !!organizationId;
  const pairAvailable = !!pair && pair.available && !!network;

  const sendDecimals = pair?.send_decimals ?? 2;
  const receiveDecimals = pair?.receive_decimals ?? 0;
  const sendBounds = useMemo<AmountBounds | null>(() => sendBoundsOf(pair), [pair]);
  const receiveBounds = useMemo<AmountBounds | null>(() => receiveBoundsOf(pair), [pair]);

  const gate = useMemo(() => {
    const input = { anchor, amount, sendDecimals, receiveDecimals, sendBounds, receiveBounds };
    return { check: amountGate(input), payload: gatedQuoteAmount(input) };
  }, [anchor, amount, sendDecimals, receiveDecimals, sendBounds, receiveBounds]);
  const amountCheck = gate.check;
  const gatedAmount = gate.payload;
  const canQuote = !!pair && organizationReady && pairAvailable && gatedAmount !== null;

  const formSignature = useMemo(
    () => [pair?.pair ?? "", organizationId ?? "-", network, anchor, canonicalAmount(amount)].join("~"),
    [pair, organizationId, network, anchor, amount],
  );

  const { requestQuote, clearQuote, isManualMode } = quote;
  /** One-shot: a keystroke deserves the debounce, a deliberate click (or a network pick) does not. */
  const immediateRef = useRef(false);
  const servedManualTickRef = useRef(0);
  const requestedRef = useRef<RequestedQuote | null>(null);
  const autoRequotedForRef = useRef<string | null>(null);
  const autoRefreshesRef = useRef(0);
  /** A wallet saved by a submit that did not reach the order, reused instead of being saved twice. */
  const savedWalletRef = useRef<{ network: string; address: string; id: string } | null>(null);

  useEffect(() => {
    if (!canQuote || !pair) {
      requestedRef.current = null;
      clearQuote();
      return;
    }
    if (isManualMode && manualTick === servedManualTickRef.current) return;
    servedManualTickRef.current = manualTick;
    const requested = requestedRef.current;
    if (!isNewQuoteRequest(requested, formSignature, manualTick)) return;
    if (requested?.signature !== formSignature) autoRefreshesRef.current = 0;
    requestedRef.current = { signature: formSignature, tick: manualTick };

    const immediate = immediateRef.current;
    immediateRef.current = false;
    requestQuote(
      {
        pair: pair.pair,
        network,
        ...gatedAmount,
        ...(isStaff && organizationId ? { organization_id: organizationId } : {}),
      },
      { immediate, signature: formSignature },
    );
  }, [
    canQuote,
    gatedAmount,
    formSignature,
    isManualMode,
    manualTick,
    requestQuote,
    clearQuote,
    pair,
    network,
    isStaff,
    organizationId,
  ]);

  const requestPrice = useCallback(() => {
    immediateRef.current = true;
    requestManualQuote();
  }, []);

  const priceNow = useCallback(() => {
    autoRefreshesRef.current = 0;
    requestPrice();
  }, [requestPrice]);

  const currentQuote = quote.quote;
  const currentQuoteRef = useRef(currentQuote);
  useEffect(() => {
    currentQuoteRef.current = currentQuote;
  }, [currentQuote]);
  const deadlineMs = quoteDeadlineMs(currentQuote);
  const deadlineIso = useMemo(
    () => (deadlineMs === null ? null : new Date(deadlineMs).toISOString()),
    [deadlineMs],
  );

  const handleQuoteExpired = useCallback(() => {
    if (!currentQuote || deadlineMs === null) return;
    setExpiredQuote({ id: currentQuote.id, atMs: deadlineMs });
    if (reviewOpen || submitPhase === "creating" || quote.isPricing || quote.isManualMode || !canQuote)
      return;
    if (autoRequotedForRef.current === currentQuote.id) return;
    if (autoRefreshesRef.current >= MAX_AUTO_REFRESHES || document.visibilityState === "hidden") return;
    autoRequotedForRef.current = currentQuote.id;
    autoRefreshesRef.current += 1;
    requestPrice();
  }, [
    currentQuote,
    deadlineMs,
    reviewOpen,
    submitPhase,
    quote.isPricing,
    quote.isManualMode,
    canQuote,
    requestPrice,
  ]);

  useQuoteExpiry(deadlineIso, handleQuoteExpired, quote.clockOffsetMs);

  const amountRejected = isRangeRejection(quote.error);

  const block = useMemo<CryptoSwapBlock>(
    () =>
      resolveCryptoSwapBlock({
        canWrite,
        organizationReady,
        pairAvailable,
        amountCheck,
        quote: currentQuote,
        isPricing: quote.isPricing,
        amountRejected,
        quoteSignature: quote.quoteSignature,
        formSignature,
        now: Math.max(
          Date.now() + quote.clockOffsetMs,
          expiredQuote && expiredQuote.id === currentQuote?.id ? expiredQuote.atMs : 0,
        ),
        walletMissing: false,
        exceedsBalance: false,
        destinationValid: refundValid,
      }),
    [
      canWrite,
      organizationReady,
      pairAvailable,
      amountCheck,
      currentQuote,
      quote.isPricing,
      amountRejected,
      quote.quoteSignature,
      quote.clockOffsetMs,
      formSignature,
      expiredQuote,
      refundValid,
    ],
  );
  const acknowledged = !!currentQuote && acknowledgedFor === currentQuote.id;
  const submitBlock = resolveSubmitBlock(block, acknowledged);

  const issueContext: AmountIssueContext | null = pair
    ? {
        side: anchor,
        sendCurrency: pair.send_currency,
        receiveCurrency: pair.receive_currency,
        sendDecimals,
        receiveDecimals,
        sendBounds,
        receiveBounds,
      }
    : null;
  // A refusal from pricing is told in the server's own words: it may be the key's bound, not the desk's.
  const serverIssue: AmountIssue | null =
    !quote.isPricing && quote.error && amountRejected
      ? { kind: "server", side: anchor, error: quote.error }
      : null;
  const anchorIssue = issueContext ? (clientAmountIssue(amountCheck, issueContext) ?? serverIssue) : null;
  const sendIssue = anchor === "send" ? anchorIssue : null;
  const receiveIssue = anchor === "receive" ? anchorIssue : null;
  const reviewIssue = block === "amount" ? anchorIssue : null;

  const addressStatus = checkCryptoSwapAddress(network, refund.address).status;
  const refundIssue: DestinationIssue | null = refundValid
    ? null
    : addressStatus !== "valid"
      ? addressStatus
      : refund.save && !refund.savedWalletId && refund.alias.trim().length > SAVED_WALLET_ALIAS_MAX_LENGTH
        ? "save_alias"
        : "concept";

  const converter = {
    anchor,
    amount,
    quote: sellConverterQuote(currentQuote),
    isPricing: quote.isPricing,
  };
  const sendField = converterField("send", converter);
  const receiveField = converterField("receive", converter);

  const setSendAmount = useCallback((value: string) => {
    setAnchor("send");
    setAmount(value);
  }, []);

  // A suggested amount is a decision already made: price it now rather than after the typing debounce.
  const applySuggestedAmount = useCallback((value: string) => {
    immediateRef.current = true;
    setAnchor("send");
    setAmount(value);
  }, []);

  const setReceiveAmount = useCallback((value: string) => {
    setAnchor("receive");
    setAmount(value);
  }, []);

  const selectNetwork = useCallback((code: string) => {
    setChosenNetwork(code);
    immediateRef.current = true;
  }, []);

  const setRefund = useCallback((values: DestinationValues, valid: boolean) => {
    setRefundState((previous) => (sameDestination(previous, values) ? previous : values));
    setRefundValid(valid);
  }, []);

  const currentQuoteId = currentQuote?.id ?? null;
  const setAcknowledged = useCallback(
    (value: boolean) => setAcknowledgedFor(value ? currentQuoteId : null),
    [currentQuoteId],
  );

  const openReview = useCallback(() => {
    if (block !== null) return;
    setAcknowledgedFor(null);
    setSubmitError(null);
    setSubmitPhase("idle");
    setReviewOpen(true);
  }, [block]);

  const closeReview = useCallback(() => {
    if (submitPhase === "creating") return;
    setReviewOpen(false);
    setSubmitError(null);
    setSubmitPhase("idle");
    if (block === "expired" && currentQuote && !quote.isManualMode) {
      autoRequotedForRef.current = currentQuote.id;
      priceNow();
    }
  }, [submitPhase, block, currentQuote, quote.isManualMode, priceNow]);

  const submit = useCallback(async () => {
    const idempotencyKey = quote.idempotencyKey;
    if (submitBlock !== null || !currentQuote || !idempotencyKey || !network) return;

    setSubmitError(null);
    const concept = refund.concept.trim();
    const address = refund.address.trim();
    const reused = savedWalletRef.current;
    let savedWalletId =
      refund.savedWalletId ||
      (refund.save && reused?.network === network && reused.address === address ? reused.id : "");
    if (!savedWalletId && refund.save) {
      try {
        const saved = await startSaveWallet((code) =>
          CryptoSwapsService.createSavedWallet(
            {
              network,
              address,
              alias:
                refund.alias.trim() ||
                defaultWalletAlias(address, (ends) => t("saved_wallet_default_alias", ends)),
              ownership: "OWN",
              ...(isStaff && organizationId ? { organization_id: organizationId } : {}),
            },
            code,
          ),
        );
        // Cancelled, or the person still has to enrol: nothing was placed.
        if (!saved) return;
        savedWalletId = saved.id;
        savedWalletRef.current = { network, address, id: saved.id };
        onWalletSaved?.(saved);
      } catch (caught) {
        setSubmitError(normalizeSellCreateError(caught));
        setSubmitPhase("failed");
        return;
      }
      // The second factor can outlast the price: never redeem a quote that lapsed or was replaced meanwhile.
      if (currentQuoteRef.current?.id !== currentQuote.id) return;
      const deadline = quoteDeadlineMs(currentQuote);
      if (deadline === null || deadline <= Date.now() + quote.clockOffsetMs) {
        setSubmitError(QUOTE_LAPSED_ERROR);
        setSubmitPhase("failed");
        clearQuote();
        priceNow();
        return;
      }
    }

    setSubmitPhase("creating");
    try {
      const sale = await CryptoSellService.createOrderOnce({
        quote_id: currentQuote.id,
        ...(savedWalletId ? { refund_saved_wallet_id: savedWalletId } : { refund_address: address }),
        idempotency_key: idempotencyKey,
        ...(concept ? { concept } : {}),
        ...(isStaff && organizationId ? { organization_id: organizationId } : {}),
      });
      navigate(cryptoSellOrderPath(sale.id));
    } catch (caught) {
      const expiresAt = Date.parse(currentQuote.expires_at);
      const quoteExpired =
        Number.isFinite(expiresAt) && expiresAt - (Date.now() + quote.clockOffsetMs) <= QUOTE_SUBMIT_GUARD_MS;
      const normalized = normalizeSellCreateError(caught, { quoteExpired });
      setSubmitError(normalized);
      setSubmitPhase("failed");
      if (isSellQuoteSpent(normalized)) {
        clearQuote();
        priceNow();
      }
    }
  }, [
    quote.idempotencyKey,
    quote.clockOffsetMs,
    submitBlock,
    currentQuote,
    network,
    refund,
    isStaff,
    organizationId,
    navigate,
    clearQuote,
    priceNow,
    startSaveWallet,
    onWalletSaved,
  ]);

  // One submission at a time: a second click during the save-wallet 2FA or the order call is ignored.
  const submitting = useRef(false);
  const submitOnce = useCallback(async () => {
    if (submitting.current) return;
    submitting.current = true;
    try {
      await submit();
    } finally {
      submitting.current = false;
    }
  }, [submit]);
  return {
    saveWalletTwoFactor,
    pair,
    networks,
    network,
    selectNetwork,
    organizationReady,
    pairAvailable,
    anchor,
    amount,
    setSendAmount,
    applySuggestedAmount,
    setReceiveAmount,
    sendField,
    receiveField,
    sendDecimals,
    receiveDecimals,
    sendBounds,
    receiveBounds,
    sendIssue,
    receiveIssue,
    reviewIssue,
    refund,
    refundValid,
    refundIssue,
    setRefund,
    quote: currentQuote,
    isPricing: quote.isPricing,
    quoteError: quote.error,
    amountRejected,
    isManualMode: quote.isManualMode,
    clockOffsetMs: quote.clockOffsetMs,
    priceNow,
    block,
    submitBlock,
    reviewOpen,
    openReview,
    closeReview,
    acknowledged,
    setAcknowledged,
    submitPhase,
    submitError,
    submit: submitOnce,
  };
}

export type UseCryptoSellFormResult = ReturnType<typeof useCryptoSellForm>;
